import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Send, 
  Loader2, 
  AlertCircle,
  Copy,
  CheckCircle,
  Video,
  Clock,
  Sparkles,
  Music,
  Zap,
  Lightbulb
} from 'lucide-react';
import { streamOpenAIResponse } from '../../lib/openai';

interface ScriptSegment {
  type: 'hook' | 'main' | 'cta';
  content: string;
  duration: string;
}

interface GeneratedScript {
  title: string;
  platform: string;
  segments: ScriptSegment[];
  musicSuggestion?: string;
  visualSuggestion?: string;
  totalDuration: string;
}

const ReelScriptGenerator = () => {
  const [topic, setTopic] = useState('');
  const [platform, setPlatform] = useState('instagram');
  const [tone, setTone] = useState('casual');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedScript, setGeneratedScript] = useState<GeneratedScript | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!topic.trim()) {
      setError('Please enter a topic for your reel');
      return;
    }

    setLoading(true);
    setError(null);
    setGeneratedScript(null);

    try {
      const prompt = `Generate a short 30-second script for a ${platform === 'instagram' ? 'Instagram Reel' : 'TikTok'} about ${topic}. 
The tone should be ${tone}.

Format your response as JSON with the following structure:
{
  "title": "Catchy title for the reel",
  "platform": "${platform}",
  "segments": [
    {
      "type": "hook",
      "content": "Attention-grabbing opening line",
      "duration": "0-5 seconds"
    },
    {
      "type": "main",
      "content": "Main content broken into 2-3 key points",
      "duration": "5-25 seconds"
    },
    {
      "type": "cta",
      "content": "Call to action",
      "duration": "25-30 seconds"
    }
  ],
  "musicSuggestion": "Type of music that would work well",
  "visualSuggestion": "Visual elements to include",
  "totalDuration": "30 seconds"
}

Make it engaging, concise, and optimized for short-form video.`;

      let responseText = '';

      await streamOpenAIResponse(
        [{ role: 'user', content: prompt }],
        (chunk) => {
          responseText += chunk;
          try {
            // Clean the response text to handle markdown code blocks
            let cleanedResponse = responseText;
            if (cleanedResponse.includes('```json')) {
              cleanedResponse = cleanedResponse.replace(/```json\n|\n```/g, '');
            } else if (cleanedResponse.includes('```')) {
              cleanedResponse = cleanedResponse.replace(/```\n|\n```/g, '');
            }
            
            const parsedData = JSON.parse(cleanedResponse);
            setGeneratedScript(parsedData);
          } catch (e) {
            // Ignore parsing errors during streaming
          }
        },
        (error) => {
          setError(error.message);
        }
      );
    } catch (err) {
      setError('Failed to generate script. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!generatedScript) return;
    
    const scriptText = `${generatedScript.title}\n\n` + 
      generatedScript.segments.map(segment => 
        `[${segment.duration}]\n${segment.content}\n`
      ).join('\n') +
      `\nMusic: ${generatedScript.musicSuggestion}\n` +
      `Visuals: ${generatedScript.visualSuggestion}`;
    
    navigator.clipboard.writeText(scriptText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getSegmentIcon = (type: string) => {
    switch (type) {
      case 'hook':
        return <Zap className="h-5 w-5 text-amber-500" />;
      case 'main':
        return <Lightbulb className="h-5 w-5 text-indigo-500" />;
      case 'cta':
        return <Sparkles className="h-5 w-5 text-green-500" />;
      default:
        return <Video className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="flex items-center space-x-4">
        <Link to="/ai-tools" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Reel Script Generator</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Create engaging 30-second scripts for Instagram Reels or TikTok videos
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Generate Script</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Modern AI Input Bar */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/5 to-purple-500/5 rounded-xl"></div>
            <div className="relative p-1 rounded-xl">
              <div className="flex items-center w-full overflow-hidden bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg focus-within:ring-2 focus-within:ring-indigo-500 dark:focus-within:ring-indigo-400 transition-all">
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., Productivity tips, Morning routine, Travel hacks"
                  className="flex-1 px-4 py-3 bg-transparent border-0 focus:outline-none focus:ring-0 text-gray-900 dark:text-white"
                />

                <div className="flex items-center pr-2 space-x-1">
                  <button
                    type="submit"
                    disabled={loading || !topic.trim()}
                    className={`p-2 rounded-md transition-colors ${
                      loading || !topic.trim()
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20'
                    }`}
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="platform" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Platform
              </label>
              <select
                id="platform"
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
              >
                <option value="instagram">Instagram Reels</option>
                <option value="tiktok">TikTok</option>
              </select>
            </div>

            <div>
              <label htmlFor="tone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tone
              </label>
              <select
                id="tone"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
              >
                <option value="casual">Casual & Conversational</option>
                <option value="professional">Professional & Informative</option>
                <option value="humorous">Humorous & Entertaining</option>
                <option value="inspirational">Inspirational & Motivational</option>
                <option value="educational">Educational & Instructional</option>
              </select>
            </div>
          </div>
        </form>
      </div>

      {generatedScript && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <Video className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
              {generatedScript.title}
            </h2>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCopy}
                className="flex items-center space-x-2 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
              >
                {copied ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    <span>Copy Script</span>
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <span className="px-2.5 py-1 bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-full text-sm">
                  {generatedScript.platform === 'instagram' ? 'Instagram Reel' : 'TikTok'}
                </span>
                <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                  <Clock className="h-4 w-4 mr-1" />
                  {generatedScript.totalDuration}
                </span>
              </div>
            </div>

            <div className="space-y-6">
              {generatedScript.segments.map((segment, index) => (
                <div 
                  key={index}
                  className={`p-4 rounded-lg border ${
                    segment.type === 'hook' 
                      ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800/30'
                      : segment.type === 'main'
                      ? 'bg-indigo-50 dark:bg-indigo-900/10 border-indigo-200 dark:border-indigo-800/30'
                      : 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800/30'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {getSegmentIcon(segment.type)}
                      <h3 className="font-medium text-gray-900 dark:text-white capitalize">
                        {segment.type === 'cta' ? 'Call to Action' : segment.type}
                      </h3>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {segment.duration}
                    </span>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
                    {segment.content}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-dark-700 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Music className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                  <h3 className="font-medium text-gray-900 dark:text-white">Music Suggestion</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300">
                  {generatedScript.musicSuggestion}
                </p>
              </div>
              
              <div className="p-4 bg-gray-50 dark:bg-dark-700 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Video className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                  <h3 className="font-medium text-gray-900 dark:text-white">Visual Suggestion</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300">
                  {generatedScript.visualSuggestion}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 text-white">
            <div className="flex items-center space-x-3 mb-4">
              <Sparkles className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Pro Tips</h3>
            </div>
            <ul className="space-y-2 text-indigo-100">
              <li className="flex items-start space-x-2">
                <span className="text-white">•</span>
                <span>Keep your delivery natural and conversational</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-white">•</span>
                <span>Use on-screen text to emphasize key points</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-white">•</span>
                <span>Film in good lighting with clear audio</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-white">•</span>
                <span>Add trending sounds or music to increase reach</span>
              </li>
            </ul>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ReelScriptGenerator;